import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, IconButton, Menu, MenuItem, Typography} from "@mui/material";
import axios from "axios";
import {toast} from "react-toastify";
import {Settings} from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const Notification = ({notification, onSetAsSeen}) => {

    const handleSeen = async () => {
        await axios.post(`/notification/notifications/seen/${notification.id}`);
        onSetAsSeen(notification.id);
    };

    const handleLinkClick = (event) => {
        event.stopPropagation();
        if (notification.link) {
            if (notification.link.startsWith("http")) {
                window.open(notification.link, "_blank");
            } else {
                window.location.href = notification.link;
            }
        }
    };

    return (
        <MenuItem
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                backgroundColor: !notification.seen ? "#e3f2fd" : "transparent",
                cursor: "pointer",
            }}
            onClick={handleSeen}
        >
            <Box sx={{display: "flex", flexDirection: "column", flex: 1}}>
                <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                        wordWrap: 'break-word',        // Allows breaking long words to wrap
                        overflowWrap: 'break-word',    // Breaks long words when necessary
                        whiteSpace: 'normal',          // Ensures text can wrap within its container
                    }}
                    dangerouslySetInnerHTML={{__html: notification.message}}
                />
                <Typography variant="caption" color="text.secondary">
                    {dayjs(notification.timeStamp).fromNow()}
                </Typography>
            </Box>

            {notification.link && (
                <Typography
                    variant="body2"
                    color="primary"
                    sx={{textDecoration: "underline", cursor: "pointer"}}
                    onClick={handleLinkClick}
                >
                    Open
                </Typography>
            )}
        </MenuItem>
    );
};

const NotificationsDropdown = ({anchorEl, onClose, showNotificationSettings}) => {

    const [notifications, setNotifications] = useState([]);

    const loadNotifications = useCallback(async () => {
        const response = await axios.get("/notification/notifications/all");
        setNotifications(response.data);
    }, []);

    useEffect(() => {
        if (anchorEl) {
            loadNotifications();
        }
    }, [anchorEl, loadNotifications]);

    const setAllAsSeen = async () => {
        const response = await axios.post("/notification/notifications/seen");
        setNotifications(notifications.map(n => ({...n, seen: true})));
        toast.success(response.data);
    };

    const deleteSeen = async () => {
        const response = await axios.delete("/notification/notifications/seen");
        setNotifications(notifications.filter(n => !n.seen));
        toast.success(response.data);
    };

    const markAsSeen = (id) => {
        setNotifications(notifications.map(n => (n.id === id ? {...n, seen: true} : n)));
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onClose}
            PaperProps={{
                elevation: 3,
                sx: {minWidth: 280, maxWidth: 450},
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center" padding={1}>
                <Typography variant="h6" sx={{fontSize: "1rem", fontWeight: "bold"}}>Notifications</Typography>
                <IconButton size="small" onClick={showNotificationSettings}>
                    <Settings fontSize="small"/>
                </IconButton>
            </Box>

            {notifications.length === 0 ? (
                <Typography padding={2} textAlign="center">You have no notifications</Typography>
            ) : (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Button size="small" onClick={setAllAsSeen}>Clear New</Button>
                        <Button size="small" onClick={deleteSeen}>Delete Seen</Button>
                    </Box>
                    <Box sx={{overflowY: 'auto', maxHeight: 400}}>
                        {notifications.map(n => (
                            <Notification key={n.id} notification={n} onSetAsSeen={markAsSeen}/>
                        ))}
                    </Box>
                </>
            )}
        </Menu>
    );
};

export default NotificationsDropdown;
